import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Row, Col, Form, Button, Card, Collapse } from "react-bootstrap";
import {
  FaSearch,
  FaFilter,
  FaTimes,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";
import {
  setFilters,
  setPage,
} from "../../../features/companies/companiesSlice";
import { AppDispatch, RootState } from "../../../store";
import { Icon } from "../../../components/common/Icon";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";

function CompaniesFilter() {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const { filters } = useSelector((state: RootState) => state.companies);

  const [localFilters, setLocalFilters] = useState({
    search: filters.search || "",
    is_active: filters.is_active === undefined ? "" : String(filters.is_active),
    agency_id: filters.agency_id || "",
  });

  const [expanded, setExpanded] = useState(false);

  // Filtre değişikliklerini izle
  useEffect(() => {
    setLocalFilters({
      search: filters.search || "",
      is_active:
        filters.is_active === undefined ? "" : String(filters.is_active),
      agency_id: filters.agency_id || "",
    });
  }, [filters]);

  // Form alanlarını güncelle
  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setLocalFilters((prev) => ({ ...prev, [name]: value }));
  };

  // Filtreleri uygula
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const newFilters = {
      search: localFilters.search || undefined,
      is_active:
        localFilters.is_active === ""
          ? undefined
          : localFilters.is_active === "true",
      agency_id: localFilters.agency_id
        ? Number(localFilters.agency_id)
        : undefined,
    };

    dispatch(setFilters(newFilters));
    dispatch(setPage(1));
  };

  // Filtreleri sıfırla
  const handleReset = () => {
    const resetFilters = {
      search: "",
      is_active: "",
      agency_id: "",
      industry: "",
      city: "",
      country: "",
    };

    setLocalFilters(resetFilters);
    dispatch(setFilters({}));
    dispatch(setPage(1));
  };

  // Aktif filtre sayısı
  const activeFiltersCount = Object.values(localFilters).filter(
    (value) => value !== ""
  ).length;

  // Theme-aware styles
  const cardStyle: React.CSSProperties = {
    background: isDark ? "rgba(30, 41, 59, 0.8)" : "rgba(255, 255, 255, 0.9)",
    border: "none",
    boxShadow: isDark
      ? "0 8px 32px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.05)"
      : "0 8px 32px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.05)",
    marginBottom: "24px",
    borderRadius: "20px",
    backdropFilter: "blur(20px)",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  };

  const headerStyle: React.CSSProperties = {
    background: "transparent",
    border: "none",
    padding: "24px",
    cursor: "pointer",
    borderRadius: expanded ? "20px 20px 0 0" : "20px",
    transition: "all 0.3s ease",
  };

  const iconContainerStyle: React.CSSProperties = {
    width: "40px",
    height: "40px",
    background: isDark ? "rgba(59, 130, 246, 0.2)" : "rgba(59, 130, 246, 0.1)",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: "16px",
    transition: "all 0.3s ease",
  };

  const titleStyle: React.CSSProperties = {
    margin: "0",
    fontWeight: "600",
    fontSize: "16px",
    color: isDark ? "#f1f5f9" : "#0f172a",
    lineHeight: "1.4",
  };

  const subtitleStyle: React.CSSProperties = {
    color: "#3b82f6",
    fontSize: "12px",
    fontWeight: "500",
    marginTop: "2px",
  };

  const labelStyle: React.CSSProperties = {
    fontWeight: "600",
    color: isDark ? "#cbd5e1" : "#374151",
    marginBottom: "8px",
    fontSize: "14px",
  };

  const inputStyle: React.CSSProperties = {
    height: "48px",
    borderRadius: "12px",
    border: "none",
    background: isDark ? "rgba(51, 65, 85, 0.8)" : "rgba(248, 250, 252, 0.8)",
    color: isDark ? "#f1f5f9" : "#0f172a",
    backdropFilter: "blur(10px)",
    transition: "all 0.3s ease",
    fontSize: "14px",
    fontWeight: "500",
  };

  const searchIconStyle: React.CSSProperties = {
    position: "absolute",
    left: "16px",
    top: "50%",
    transform: "translateY(-50%)",
    color: isDark ? "#64748b" : "#9ca3af",
    fontSize: "16px",
    pointerEvents: "none",
  };

  const buttonStyle = (variant: "primary" | "outline") => ({
    padding: "12px 24px",
    borderRadius: "12px",
    border: "none",
    fontWeight: "600",
    fontSize: "14px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    transition: "all 0.3s ease",
    ...(variant === "primary"
      ? {
          background: "linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)",
          color: "white",
          boxShadow: "0 4px 12px rgba(59, 130, 246, 0.25)",
        }
      : {
          background: isDark
            ? "rgba(51, 65, 85, 0.8)"
            : "rgba(248, 250, 252, 0.8)",
          color: isDark ? "#cbd5e1" : "#6b7280",
          border: isDark
            ? "1px solid rgba(255, 255, 255, 0.1)"
            : "1px solid rgba(0, 0, 0, 0.1)",
        }),
  });

  return (
    <Card style={cardStyle}>
      <Card.Header
        style={headerStyle}
        onClick={() => setExpanded(!expanded)}
        onMouseEnter={(e) => {
          if (!expanded) {
            e.currentTarget.style.background = isDark
              ? "rgba(51, 65, 85, 0.3)"
              : "rgba(248, 250, 252, 0.5)";
          }
        }}
        onMouseLeave={(e) => {
          if (!expanded) {
            e.currentTarget.style.background = "transparent";
          }
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            <div
              style={iconContainerStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "scale(1.05)";
                e.currentTarget.style.background = isDark
                  ? "rgba(59, 130, 246, 0.3)"
                  : "rgba(59, 130, 246, 0.15)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "scale(1)";
                e.currentTarget.style.background = isDark
                  ? "rgba(59, 130, 246, 0.2)"
                  : "rgba(59, 130, 246, 0.1)";
              }}
            >
              <i className="bi bi-funnel-fill" />
            </div>
            <div>
              <h6 style={titleStyle}>{t("advanced_filters")}</h6>
              {activeFiltersCount > 0 && (
                <small style={subtitleStyle}>
                  {activeFiltersCount} {t("active_filters")}
                </small>
              )}
            </div>
          </div>

          <i
            className="bi bi-chevron-down"
            style={{
              color: isDark ? "#64748b" : "#9ca3af",
              fontSize: "14px",
              transition: "all 0.3s ease",
              transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
            }}
          />
        </div>
      </Card.Header>

      <Collapse in={expanded}>
        <div>
          <Card.Body
            style={{
              paddingTop: "0",
              paddingLeft: "24px",
              paddingRight: "24px",
              paddingBottom: "24px",
            }}
          >
            <Form onSubmit={handleSubmit}>
              {/* Search Row */}
              <Row className="mb-4">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label style={labelStyle}>{t("search")}</Form.Label>
                    <div style={{ position: "relative" }}>
                      <Form.Control
                        type="text"
                        name="search"
                        value={localFilters.search}
                        onChange={handleChange}
                        placeholder={t("search_companies_placeholder")}
                        style={{
                          ...inputStyle,
                          paddingLeft: "48px",
                        }}
                        onFocus={(e) => {
                          e.target.style.boxShadow =
                            "0 0 0 0.2rem rgba(59, 130, 246, 0.25)";
                          e.target.style.background = isDark
                            ? "rgba(51, 65, 85, 1)"
                            : "rgba(255, 255, 255, 1)";
                        }}
                        onBlur={(e) => {
                          e.target.style.boxShadow = "none";
                          e.target.style.background = isDark
                            ? "rgba(51, 65, 85, 0.8)"
                            : "rgba(248, 250, 252, 0.8)";
                        }}
                      />
                      <i className="bi bi-search" style={searchIconStyle} />
                    </div>
                  </Form.Group>
                </Col>
              </Row>

              {/* Filter Options */}
              <Row className="mb-4">
                <Col md={3}>
                  <Form.Group>
                    <Form.Label style={labelStyle}>{t("status")}</Form.Label>
                    <Form.Select
                      name="is_active"
                      value={localFilters.is_active}
                      onChange={handleChange}
                      style={inputStyle}
                      onFocus={(e) => {
                        e.target.style.boxShadow =
                          "0 0 0 0.2rem rgba(59, 130, 246, 0.25)";
                        e.target.style.background = isDark
                          ? "rgba(51, 65, 85, 1)"
                          : "rgba(255, 255, 255, 1)";
                      }}
                      onBlur={(e) => {
                        e.target.style.boxShadow = "none";
                        e.target.style.background = isDark
                          ? "rgba(51, 65, 85, 0.8)"
                          : "rgba(248, 250, 252, 0.8)";
                      }}
                    >
                      <option value="">{t("all_statuses")}</option>
                      <option value="true">{t("active")}</option>
                      <option value="false">{t("inactive")}</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              {/* Action Buttons */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "12px",
                  flexWrap: "wrap",
                }}
              >
                <Button
                  variant="outline-secondary"
                  onClick={handleReset}
                  disabled={activeFiltersCount === 0}
                  style={{
                    ...buttonStyle("outline"),
                    opacity: activeFiltersCount === 0 ? 0.5 : 1,
                    cursor:
                      activeFiltersCount === 0 ? "not-allowed" : "pointer",
                  }}
                  onMouseEnter={(e) => {
                    if (activeFiltersCount > 0) {
                      e.currentTarget.style.transform = "translateY(-2px)";
                      e.currentTarget.style.boxShadow = isDark
                        ? "0 8px 16px rgba(0, 0, 0, 0.3)"
                        : "0 8px 16px rgba(0, 0, 0, 0.1)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (activeFiltersCount > 0) {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "none";
                    }
                  }}
                >
                  <Icon icon={FaTimes} />
                  {t("reset")}
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  style={buttonStyle("primary")}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow =
                      "0 8px 20px rgba(59, 130, 246, 0.35)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow =
                      "0 4px 12px rgba(59, 130, 246, 0.25)";
                  }}
                >
                  <Icon icon={FaSearch} />
                  {t("apply_filters")}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </div>
      </Collapse>

      {/* Global Styles */}
      <style>
        {`
          /* Custom focus styles for better accessibility */
          .form-control:focus,
          .form-select:focus {
            outline: 2px solid ${isDark ? "#60a5fa" : "#3b82f6"};
            outline-offset: 2px;
          }
          
          /* Smooth transitions */
          .form-control,
          .form-select,
          .btn {
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          }
          
          /* Placeholder styling */
          .form-control::placeholder {
            color: ${isDark ? "#64748b" : "#9ca3af"};
            opacity: 0.8;
          }
          
          /* Select arrow styling for dark theme */
          .form-select {
            background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='${isDark ? "%23cbd5e1" : "%236b7280"}' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m1 6 7 7 7-7'/%3e%3c/svg%3e");
          }
          
          /* Responsive adjustments */
          @media (max-width: 768px) {
            .d-flex.justify-content-end {
              flex-direction: column;
              align-items: stretch;
            }
            
            .d-flex.justify-content-end .btn {
              margin-bottom: 8px;
            }
            
            .d-flex.justify-content-end .btn:last-child {
              margin-bottom: 0;
            }
          }
        `}
      </style>
    </Card>
  );
}

export default CompaniesFilter;
